import { useEffect } from "react";
import { IoArrowDown, IoArrowUp } from "react-icons/io5";
import {
    compareHrpReconciliationRateAmounts,
    formatHrpReconciliationAmount,
    formatHrpReconciliationEffectiveDate,
    formatHrpReconciliationRateType,
    getHrpReconciliationActionLabel,
    getHrpReconciliationActionOptions,
    getHrpReconciliationExtractedLabel,
    groupHrpReconciliationDrawerRows,
    HRP_RECONCILIATION_MATCH_LABELS,
    resolveHrpReconciliationExtractedRateType,
} from "../../utils/billingRateUtils";
import HrpReconciliationBulkActions from "./HrpReconciliationBulkActions";

const RATE_COMPARISON_LABELS = {
    higher: "Extracted rate is higher than current HRP rate",
    lower: "Extracted rate is lower than current HRP rate",
    equal: "Extracted rate matches current HRP rate",
};

const RateComparisonIndicator = ({ comparison }) => {
    if (!comparison) return null;

    if (comparison === "equal") {
        return (
            <span
                className="billing-rate-builder__hrp-rate-indicator billing-rate-builder__hrp-rate-indicator--equal"
                aria-label={RATE_COMPARISON_LABELS.equal}
                title={RATE_COMPARISON_LABELS.equal}
            >
                =
            </span>
        );
    }

    const Icon = comparison === "higher" ? IoArrowUp : IoArrowDown;
    return (
        <span
            className={`billing-rate-builder__hrp-rate-indicator billing-rate-builder__hrp-rate-indicator--${comparison}`}
            aria-label={RATE_COMPARISON_LABELS[comparison]}
            title={RATE_COMPARISON_LABELS[comparison]}
        >
            <Icon aria-hidden="true" size={22} />
        </span>
    );
};

const AmountCell = ({ amount, feeType, compareToAmount = null, showComparison = true }) => {
    const formatted = formatHrpReconciliationAmount(amount);
    const rateType = formatHrpReconciliationRateType(feeType);
    const comparison = showComparison && compareToAmount != null
        ? compareHrpReconciliationRateAmounts(compareToAmount, amount)
        : null;
    if (!formatted) return <span className="billing-rate-builder__muted">—</span>;
    return (
        <div className="billing-rate-builder__hrp-amount-cell">
            <div className={[
                "billing-rate-builder__hrp-amount-main",
                comparison ? "billing-rate-builder__hrp-amount-main--with-indicator" : "",
            ].filter(Boolean).join(" ")}>
                <div>
                    <div className="billing-rate-builder__hrp-amount-value">{formatted}</div>
                    {rateType && (
                        <div className="billing-rate-builder__hrp-amount-type">{rateType}</div>
                    )}
                </div>
                {comparison && <RateComparisonIndicator comparison={comparison} />}
            </div>
        </div>
    );
};

const formatHrpBillingMethodRateType = (billingMethod) => {
    const method = String(billingMethod ?? "").trim().toLowerCase();
    if (method === "pspm") return "PSPM";
    if (method === "pmpm") return "PMPM";
    return billingMethod ? String(billingMethod).toUpperCase() : null;
};

const amountsDiffer = (left, right) => {
    if (left == null || right == null || left === "" || right === "") return false;
    const a = Number(left);
    const b = Number(right);
    if (Number.isNaN(a) || Number.isNaN(b)) return String(left) !== String(right);
    return a !== b;
};

const MatchBadge = ({ matchType }) => {
    const base = matchType?.includes("renewal-only")
        ? "unmatched"
        : matchType?.split("+")[0] ?? matchType;
    return (
        <span className={`billing-rate-builder__confidence-badge billing-rate-builder__confidence-badge--${base}`}>
            {HRP_RECONCILIATION_MATCH_LABELS[matchType] ?? matchType}
        </span>
    );
};

const ActionCell = ({ row, value, onChange, preferKeep, planType, readOnly, excludeActions = [] }) => {
    if (readOnly) {
        return (
            <span className="billing-rate-builder__hrp-action-label">
                {getHrpReconciliationActionLabel(row)}
            </span>
        );
    }

    const options = getHrpReconciliationActionOptions(row, { preferKeep, excludeActions });
    const selected = options.some((option) => option.value === value)
        ? value
        : options[0]?.value;

    return (
        <select
            className="billing-rate-builder__hrp-action-select"
            value={selected}
            onChange={(e) => onChange?.(row.rowId, e.target.value)}
            aria-label={`Action for ${getHrpReconciliationExtractedLabel(row, { planType }) || row.hrpComponentName || row.rowId}`}
        >
            {options.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
        </select>
    );
};

const ReconciliationSection = ({
    title,
    subtitle,
    rows,
    preferKeep,
    highlightRowId,
    getRowStatus,
    draftActions,
    onDraftActionChange,
    hideHeader = false,
    planType,
    readOnly = false,
    showSectionBulkActions = false,
    onBulkActionForRows,
    excludeBulkActions = [],
    showEffectiveDateColumn = false,
    showRateIndicators = true,
}) => {
    useEffect(() => {
        if (!highlightRowId || !rows.some((r) => r.rowId === highlightRowId)) return;
        const el = document.getElementById(`hrp-row-${highlightRowId}`);
        el?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, [highlightRowId, rows]);

    if (!rows.length) return null;

    return (
        <section className="billing-rate-builder__hrp-reconciliation-section">
            {!hideHeader && (
                <header className="billing-rate-builder__hrp-reconciliation-section-header">
                    <div className="billing-rate-builder__hrp-reconciliation-section-heading">
                        <h3>{title}</h3>
                        {subtitle && <p>{subtitle}</p>}
                    </div>
                    {showSectionBulkActions && onBulkActionForRows && (
                        <HrpReconciliationBulkActions
                            rows={rows}
                            onBulkAction={(bulkAction) => onBulkActionForRows(rows, bulkAction)}
                            excludeBulkActions={excludeBulkActions}
                            className="billing-rate-builder__hrp-section-bulk-actions"
                        />
                    )}
                </header>
            )}
            <div className="billing-rate-builder__hrp-reconciliation-table-wrap">
                <table className="billing-rate-builder__hrp-reconciliation-table">
                    <thead>
                        <tr>
                            <th>Match</th>
                            <th>In HRP</th>
                            <th>Extracted</th>
                            <th className="billing-rate-builder__hrp-reconciliation-rate-col">Current HRP Rate</th>
                            <th className="billing-rate-builder__hrp-reconciliation-rate-col">Extracted rate</th>
                            {showEffectiveDateColumn && (
                                <>
                                    <th className="billing-rate-builder__hrp-reconciliation-effective-date-col">
                                        HRP effective date
                                    </th>
                                    <th className="billing-rate-builder__hrp-reconciliation-effective-date-col">
                                        Upload effective date
                                    </th>
                                </>
                            )}
                            <th className="billing-rate-builder__hrp-reconciliation-status-col">Status</th>
                            <th className="billing-rate-builder__hrp-reconciliation-action-col">{readOnly ? "Decision" : "Action"}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row) => {
                            const extractedLabel = getHrpReconciliationExtractedLabel(row, { planType });
                            const hrpRateAmount = row.hrpCurrentAmount ?? null;
                            const extractedRateAmount = row.extractedAmount ?? row.renewalCurrentAmount ?? null;
                            const rateMismatch = amountsDiffer(hrpRateAmount, extractedRateAmount);
                            const isIgnored = row.skipped || row.userAction === "skip";
                            const isKept = row.userAction === "keep";

                            return (
                                <tr
                                    key={row.rowId}
                                    id={`hrp-row-${row.rowId}`}
                                    className={[
                                        row.rowId === highlightRowId ? "billing-rate-builder__hrp-row--highlight" : "",
                                        row.nameMismatch && !row.confirmed ? "billing-rate-builder__hrp-row--mismatch" : "",
                                        rateMismatch && !row.confirmed ? "billing-rate-builder__hrp-row--rate-mismatch" : "",
                                        isIgnored ? "billing-rate-builder__hrp-row--ignored" : "",
                                        isKept ? "billing-rate-builder__hrp-row--kept" : "",
                                    ].filter(Boolean).join(" ")}
                                >
                                    <td>
                                        <MatchBadge matchType={row.matchType} />
                                        {row.matchScore != null && (
                                            row.matchType?.includes("fuzzy")
                                            || row.matchType?.startsWith("cbc")
                                        ) && (
                                            <div className="billing-rate-builder__muted">
                                                {Math.round(row.matchScore * 100)}% name match
                                            </div>
                                        )}
                                    </td>
                                    <td>
                                        {row.hrpComponentName ? (
                                            <strong>{row.hrpComponentName}</strong>
                                        ) : (
                                            <span className="billing-rate-builder__muted">—</span>
                                        )}
                                    </td>
                                    <td>
                                        {extractedLabel ? (
                                            <>
                                                <strong>{extractedLabel}</strong>
                                                {row.nameMismatch && !isKept && (
                                                    <div className="billing-rate-builder__hrp-name-warning">Name differs from HRP</div>
                                                )}
                                                {isKept && (
                                                    <div className="billing-rate-builder__hrp-kept-badge">Using current HRP name</div>
                                                )}
                                            </>
                                        ) : (
                                            <span className="billing-rate-builder__muted">—</span>
                                        )}
                                    </td>
                                    <td className="billing-rate-builder__hrp-reconciliation-rate-col">
                                        <AmountCell
                                            amount={hrpRateAmount}
                                            feeType={formatHrpBillingMethodRateType(row.hrpBillingMethod)}
                                        />
                                    </td>
                                    <td className="billing-rate-builder__hrp-reconciliation-rate-col">
                                        <AmountCell
                                            amount={extractedRateAmount}
                                            feeType={resolveHrpReconciliationExtractedRateType(row)}
                                            compareToAmount={hrpRateAmount}
                                            showComparison={showRateIndicators}
                                        />
                                    </td>
                                    {showEffectiveDateColumn && (
                                        <>
                                            <td className="billing-rate-builder__hrp-reconciliation-effective-date-col">
                                                {row.hrpEffectiveDate ?? (
                                                    <span className="billing-rate-builder__muted">—</span>
                                                )}
                                            </td>
                                            <td className="billing-rate-builder__hrp-reconciliation-effective-date-col">
                                                {formatHrpReconciliationEffectiveDate(row.extractedEffectiveDate) ?? (
                                                    <span className="billing-rate-builder__muted">—</span>
                                                )}
                                            </td>
                                        </>
                                    )}
                                    <td className="billing-rate-builder__hrp-reconciliation-status-col">{getRowStatus(row)}</td>
                                    <td className="billing-rate-builder__hrp-reconciliation-action-col">
                                        <ActionCell
                                            row={row}
                                            value={draftActions?.[row.rowId]}
                                            onChange={onDraftActionChange}
                                            preferKeep={preferKeep}
                                            planType={planType}
                                            readOnly={readOnly}
                                            excludeActions={excludeBulkActions}
                                        />
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </section>
    );
};

const BillingRateBuilderHrpReconciliation = ({
    hrpSnapshot,
    reconciliationRows,
    needsReview,
    isReady,
    getRowStatus,
    highlightRowId = null,
    layout = "inline",
    planType = "aso",
    draftActions,
    onDraftActionChange,
    readOnly = false,
    onBulkActionForRows,
}) => {
    const components = hrpSnapshot?.components ?? [];
    if (components.length === 0 || !reconciliationRows?.length) return null;

    const reviewRows = reconciliationRows.filter(needsReview);
    const readyRows = reconciliationRows.filter(isReady);
    const resolvedRows = reconciliationRows.filter((r) => r.confirmed || r.skipped);
    const pending = reconciliationRows.filter((r) => !r.confirmed && !r.skipped);
    const renewalRowCount = reconciliationRows.filter((r) => r.renewalFeeIndex != null).length;
    const asOfLabel = hrpSnapshot?.asOfDate ? ` as of ${hrpSnapshot.asOfDate}` : "";
    const inDrawer = layout === "drawer";

    const sections = inDrawer
        ? groupHrpReconciliationDrawerRows(reconciliationRows, needsReview)
        : [
            {
                key: "review",
                title: `Needs review (${reviewRows.length})`,
                subtitle: "Fuzzy links and HRP + renewal conflicts.",
                rows: reviewRows,
                preferKeep: true,
            },
            {
                key: "ready",
                title: `Ready to confirm (${readyRows.length})`,
                subtitle: "High-confidence matches.",
                rows: readyRows,
                preferKeep: false,
            },
            {
                key: "resolved",
                title: `Saved (${resolvedRows.length})`,
                subtitle: "Completed decisions.",
                rows: resolvedRows,
                preferKeep: false,
            },
        ].filter((section) => section.rows.length > 0);

    const hideSectionHeaders = !inDrawer && sections.length === 1;

    return (
        <div className={[
            "billing-rate-builder__hrp-reconciliation",
            inDrawer ? "billing-rate-builder__hrp-reconciliation--drawer" : "billing-rate-builder__alert",
        ].join(" ")}>
            {!inDrawer && (
                <div className="billing-rate-builder__page-header">
                    <div>
                        <p className="title billing-rate-builder__alert-title">HRP updates{asOfLabel}</p>
                        <p className="billing-rate-builder__alert-body-plain">
                            This account already has components in HRP. Choose an action for each row, then save.
                            {renewalRowCount > 0 ? ` Includes ${renewalRowCount} renewal document fee${renewalRowCount !== 1 ? "s" : ""}.` : ""}
                            {pending.length > 0 ? ` ${pending.length} pending.` : " All rows resolved."}
                        </p>
                    </div>
                </div>
            )}

            {sections.map((section) => (
                <ReconciliationSection
                    key={section.key}
                    title={section.title}
                    subtitle={section.subtitle}
                    rows={section.rows}
                    preferKeep={section.preferKeep}
                    hideHeader={hideSectionHeaders}
                    highlightRowId={highlightRowId}
                    getRowStatus={getRowStatus}
                    draftActions={draftActions}
                    onDraftActionChange={onDraftActionChange}
                    planType={planType}
                    readOnly={readOnly}
                    showSectionBulkActions={inDrawer && !readOnly}
                    onBulkActionForRows={onBulkActionForRows}
                    excludeBulkActions={section.excludeBulkActions}
                    showEffectiveDateColumn={section.showEffectiveDateColumn}
                    showRateIndicators={!inDrawer}
                />
            ))}
        </div>
    );
};

export default BillingRateBuilderHrpReconciliation;
